import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { showConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { shiftService } from '@/services/shiftService'
import type { ShiftListItem } from '@/api/types'

export const ShiftList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [shifts, setShifts] = useState<ShiftListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await shiftService.getAll(branchId, pageNumber, pageSize)
        setShifts(res.data ?? [])
        const tr = res.totalRecords ?? res.data?.length ?? 0
        setTotalRecords(tr)
        const tp = tr ? Math.ceil(tr / pageSize) : 0
        setTotalPages(tp)
      } catch (e: any) {
        toast.error(e?.response?.data?.message || t('shiftPage.fetchFailed'))
        setShifts([])
        setTotalRecords(0)
        setTotalPages(0)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize])

  const filteredShifts = useMemo(() => {
    if (!searchQuery.trim()) return shifts
    const q = searchQuery.toLowerCase()
    return shifts.filter(
      (s) =>
        (s.shiftDescription ?? s.description ?? '').toLowerCase().includes(q) ||
        (s.name ?? s.shiftType ?? '').toLowerCase().includes(q)
    )
  }, [shifts, searchQuery])

  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)
  const pageTotal = Math.max(1, totalPages || 1)

  const handleDelete = async (row: ShiftListItem) => {
    const shiftId = row.id
    if (shiftId == null) return
    const name = row.name ?? row.shiftType ?? row.shiftDescription ?? row.description ?? '—'
    const confirmed = await showConfirm(
      t('shiftPage.deleteConfirmTitle'),
      t('shiftPage.deleteConfirmMessage', { name }),
      t('shiftPage.deleteConfirmYes'),
      t('shiftPage.cancel')
    )
    if (!confirmed) return
    try {
      await shiftService.delete(shiftId)
      await showSuccess(t('shiftPage.deletedTitle'), t('shiftPage.deletedMessage'))
      if (branchId) {
        const res = await shiftService.getAll(branchId, pageNumber, pageSize)
        setShifts(res.data ?? [])
        const tr = res.totalRecords ?? res.data?.length ?? 0
        setTotalRecords(tr)
        const tp = tr ? Math.ceil(tr / pageSize) : 0
        setTotalPages(tp)
      }
    } catch (e: any) {
      await showError(t('shiftPage.deleteFailedTitle'), e?.response?.data?.message || t('shiftPage.deleteFailedMessage'))
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('shiftPage.notAvailable')
    try {
      return new Date(dateString).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return t('shiftPage.timePlaceholder')
    if (timeStr.length <= 5 && timeStr.includes(':')) return timeStr
    try {
      const d = new Date(timeStr)
      return d.toTimeString().slice(0, 5)
    } catch {
      return timeStr
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('shiftPage.pageTitle')} description={t('shiftPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('shiftPage.branchRequiredTitle')}
              description={t('shiftPage.branchRequiredDescriptionList')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('shiftPage.pageTitle')}
        description={t('shiftPage.pageDescription')}
        action={{
          label: t('shiftPage.addShift'),
          onClick: () => navigate('/shifts/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('shiftPage.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('shiftPage.pageSize')}</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <ListLoader message={t('shiftPage.loading')} />
          ) : filteredShifts.length === 0 ? (
            <EmptyState
              title={searchQuery ? t('shiftPage.emptyNoShifts') : t('shiftPage.emptyNoCreated')}
              description={searchQuery ? t('shiftPage.emptySearchHint') : t('shiftPage.emptyAddFirst')}
              action={
                !searchQuery
                  ? { label: t('shiftPage.addShift'), onClick: () => navigate('/shifts/new') }
                  : undefined
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('shiftPage.colShiftName')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('shiftPage.colStartTime')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('shiftPage.colEndTime')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('shiftPage.colCreatedDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[120px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('shiftPage.colAction')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredShifts.map((row) => (
                      <TableRow
                        key={row.id ?? row.description ?? row.shiftDescription}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {row.name ?? row.shiftType ?? row.shiftDescription ?? row.description ?? '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatTime(row.shiftStartTime ?? row.startTime)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatTime(row.shiftEndTime ?? row.endTime)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(row.createdDate)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/shifts/${row.id}/edit`)}
                              title={t('shiftPage.editTitle')}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(row)}
                              title={t('shiftPage.deleteTitle')}
                              className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="border-t px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  {t('shiftPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" /> {t('shiftPage.previous')}
                  </Button>
                  <span className="text-sm">
                    {t('shiftPage.pageOf', { page: pageNumber, totalPages: pageTotal })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.min(totalPages || 1, p + 1))}
                    disabled={totalPages === 0 || pageNumber >= totalPages}
                  >
                    {t('shiftPage.next')} <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
